<template>
  <v-dialog
    max-width="1200px"
    persistent
    v-model="dialog"
    scrollable
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <v-card
      class="address-management-card"
      v-if="selectedCustomer != null && selectedCustomer.user_address"
    >
      <!-- Modern Header -->
      <div class="address-header">
        <div class="header-icon">
          <v-icon color="#fddf00" size="32">location_on</v-icon>
        </div>
        <div class="header-content">
          <h2 class="header-title">Address Management</h2>
          <p class="header-subtitle">Manage addresses for customer</p>
        </div>
        <div class="customer-info pr-5">
          <v-chip color="grey lighten-3" small>
            <v-icon left size="12">person</v-icon>
            {{ selectedCustomer.user_first_name }}
            {{ selectedCustomer.user_last_name }}
          </v-chip>
          <v-chip v-if="selectedCustomer.user_vip" color="#fddf00" small>
            <v-icon left size="12">star</v-icon>
            VIP
          </v-chip>
        </div>
        <v-btn
          icon
          @click="$emit('setDialog', false) && $emit('cancel')"
          class="close-btn"
        >
          <v-icon>close</v-icon>
        </v-btn>
      </div>

      <v-divider></v-divider>

      <!-- Content -->
      <v-card-text class="address-content">
        <!-- Address List Section - Full Width -->
        <div class="address-list-section">
          <div class="section-header">
            <h3 class="section-title">
              <v-icon color="#fddf00" left>list</v-icon>
              Saved Addresses
              <v-chip color="grey lighten-2" small class="address-count">
                {{ selectedCustomer.user_address.length }}
              </v-chip>
            </h3>

            <!-- Add New Address Button -->
            <v-btn
              color="#fddf00"
              @click="openNewAddressDialog"
              :disabled="isEditCustomer"
              class="add-address-btn"
              :class="{ 'add-address-btn--mobile': $vuetify.breakpoint.xs }"
            >
              <v-icon :class="{ 'mr-2': !$vuetify.breakpoint.xs }">add</v-icon>
              <span v-if="!$vuetify.breakpoint.xs" class="add-btn-text"
                >Add New Address</span
              >
            </v-btn>
          </div>

          <div class="address-list">
            <div
              v-if="selectedCustomer.user_address.length === 0"
              class="no-addresses"
            >
              <v-icon color="grey" size="48">location_off</v-icon>
              <h4 class="no-addresses-title">No Addresses</h4>
              <p class="no-addresses-text">
                This customer hasn't added any addresses yet.
              </p>
              <v-btn
                color="#fddf00"
                @click="openNewAddressDialog"
                :disabled="isEditCustomer"
                class="no-addresses-btn"
              >
                <v-icon left>add_location</v-icon>
                Add First Address
              </v-btn>
            </div>

            <div v-else class="address-grid">
              <addressListDetail
                v-for="(address, index) in selectedCustomer.user_address"
                :key="index"
                :info="address"
                :selectedCustomer="selectedCustomer"
                class="address-item"
                :provinceItems="provinceItems"
              />
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- New Address Dialog -->
    <newAddressDialog
      :dialog="newAddressDialog"
      :selectedCustomer="selectedCustomer"
      @setDialog="newAddressDialog = $event"
      @address-added="handleAddressAdded"
    />
  </v-dialog>
</template>

<script>
import addressListDetail from "./AddressListDetail";
import newAddressDialog from "./NewAddressDialog";
import { mapGetters } from "vuex";

export default {
  props: ["dialog", "selectedCustomer"],
  components: {
    addressListDetail,
    newAddressDialog,
  },
  data() {
    return {
      newAddressDialog: false,
      provinceItems: [],
    };
  },
  computed: {
    ...mapGetters(["isEditCustomer"]),
  },
  methods: {
    openNewAddressDialog() {
      this.newAddressDialog = true;
    },

    handleAddressAdded() {
      // Refresh the address list or handle the new address
      // The address should already be added to the store
      this.$forceUpdate();
    },
  },
  async created() {
    this.provinceItems = await this.$store.dispatch(
      "fetchCustomerBack/getProvinceEdit"
    );
  },
};
</script>

<style scoped>
.address-management-card {
  border-radius: 16px;
  overflow: hidden;
}

.address-header {
  display: flex;
  align-items: center;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, #fddf00 0%, #f39c12 100%);
  gap: 1rem;
  position: relative;
}

.header-icon {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-content {
  flex: 1;
}

.header-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: black;
  margin: 0;
}

.header-subtitle {
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.8);
  margin: 0.25rem 0 0 0;
}

.customer-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
}

.close-btn {
  background: rgba(0, 0, 0, 0.1) !important;
  color: black !important;
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
}

.address-content {
  padding: 2rem;
}

.address-list-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
  width: 100%;
}

.address-list {
  /* Remove max-height and overflow restrictions */
  margin-right: 0;
  padding-right: 0;
}

.address-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.add-address-btn {
  color: black !important;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  min-width: auto;
}

.add-address-btn--mobile {
  min-width: 48px !important;
  width: 48px;
  height: 48px;
  border-radius: 50% !important;
  padding: 0 !important;
}

.add-btn-text {
  white-space: nowrap;
}

.address-count {
  margin-left: 0.5rem;
  font-weight: 600;
}

.address-item {
  margin-bottom: 1rem;
}

.address-item:last-child {
  margin-bottom: 0;
}

.no-addresses {
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
}

.no-addresses-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0.75rem 0 0.5rem 0;
}

.no-addresses-text {
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.4;
}

.no-addresses-btn {
  margin-top: 1rem;
  color: black !important;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}

/* Mobile Responsiveness */
@media (max-width: 1024px) {
  .address-layout {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .add-address-section {
    order: 1;
  }

  .address-list-section {
    order: 2;
  }
}

@media (max-width: 768px) {
  .address-header {
    padding: 1rem 1.5rem;
    padding-top: 3rem; /* Add top padding for close button */
    flex-wrap: wrap;
  }

  .close-btn {
    top: 0.75rem;
    right: 0.75rem;
  }

  .customer-info {
    width: 100%;
    flex-direction: row;
    justify-content: flex-start;
    margin-top: 0.5rem;
    gap: 0.5rem;
  }

  .address-content {
    padding: 1.5rem;
  }

  .address-list-section,
  .add-address-section {
    padding: 1rem;
  }

  .address-grid {
    grid-template-columns: 1fr;
  }

  .section-title {
    align-self: flex-start;
  }

  .add-address-btn {
    align-self: center;
  }
}

@media (max-width: 480px) {
  .address-header {
    padding: 0.75rem 1rem;
    padding-top: 2.5rem;
  }

  .close-btn {
    top: 0.5rem;
    right: 0.5rem;
    width: 40px;
    height: 40px;
  }

  .header-title {
    font-size: 1.3rem;
  }

  .header-subtitle {
    font-size: 0.8rem;
  }

  .customer-info {
    gap: 0.25rem;
  }

  .customer-info .v-chip {
    font-size: 0.75rem;
  }
}
</style>